package components.accounting

import it.neckar.commons.kotlin.js.safeGet
import it.neckar.datetime.minimal.LocalDate
import it.neckar.datetime.minimal.Month
import it.neckar.datetime.minimal.Year
import it.neckar.open.collections.fastForEach
import it.neckar.open.kotlin.lang.SpecialChars
import it.neckar.react.common.*
import it.neckar.react.common.table.*
import plannerI18nConfiguration
import react.*
import react.dom.*
import services.http.CompanyRevenue

/**
 * Collects the projects for each year.
 * Visualizes the sum of the earnings for each year.
 *
 */
val RevenueTable: FC<RevenueTableProps> = fc("RevenueTable") { props ->
  val lizergyRevenue = props::lizergyRevenue.safeGet()
  val neckarITRevenue = props::neckarITRevenue.safeGet()
  //val partnerRevenues = props::partnerRevenues.safeGet()

  val allRevenues = buildList {
    lizergyRevenue?.let { add(it) }
    neckarITRevenue?.let { add(it) }
    //partnerRevenues?.let { addAll(it) }
  }

  val sortedByFunction = useState(
    SortedByFunction(
      sortedBy = SortedBy.SortedDescending,
      tableDataColumn = totalRevenueColumn,
    )
  )

  val dayColumns = buildList {
    allRevenues.flatMap { it.revenueByDay.keys }.distinct().sortedBy { it }.fastForEach { date ->
      add(getDailyCompanyRevenueColumn(date))
    }
  }

  val monthColumns = buildList {
    allRevenues.flatMap { it.revenueByMonth.keys }.distinct().sortedBy { it?.value }.fastForEach { month ->
      add(getMonthlyCompanyRevenueColumn(month))
    }
  }

  val yearColumns = buildList {
    allRevenues.flatMap { it.revenueByYear.keys }.distinct().sortedBy { it?.value }.fastForEach { year ->
      add(getYearlyCompanyRevenueColumn(year))
    }
  }

  val columns = buildList {
    add(companyColumn)
    monthColumns.fastForEach { add(it) }
    add(totalRevenueColumn)
  }


  div("my-3") {

    busyIfNull(lizergyRevenue) { loadedLizergyRevenue ->
      table(
        columns = columns,
        entries = allRevenues,
        footerEntry = CompanyRevenue("Gesamt", allRevenues.flatMap { it.revenues }),
        sortedByFunction = sortedByFunction,
      ) {}
    }

  }

}


fun getDailyCompanyRevenueColumn(date: LocalDate?): TableDataColumn<CompanyRevenue> {
  return TableDataColumn(
    id = "$date",
    title = date?.format() ?: "Nicht abgerechnet",
    titleClasses = "justify-content-end",
    sortFunction = compareBy { it.revenueByDay[date] },
  ) { companyRevenue ->
    {
      attrs {
        addClass("text-end")
      }

      +buildString {
        companyRevenue.revenueByDay[date]?.let { revenue ->
          appendLine(revenue.format(plannerI18nConfiguration))
        }
        if (companyRevenue.companyName == "Neckar IT") {
          companyRevenue.projectCountsByDay[date]?.let { count ->
            appendLine("($count${SpecialChars.Nbsp}Projekte)")
          }
        }
      }
    }
  }
}

fun getMonthlyCompanyRevenueColumn(month: Month?): TableDataColumn<CompanyRevenue> {
  return TableDataColumn(
    id = "$month.",
    title = month?.format() ?: "Nicht abgerechnet",
    titleClasses = "justify-content-end",
    sortFunction = compareBy { it.revenueByMonth[month] },
  ) { companyRevenue ->
    {
      attrs {
        addClass("text-end")
      }

      +buildString {
        companyRevenue.revenueByMonth[month]?.let { revenue ->
          appendLine(revenue.format(plannerI18nConfiguration))
        }
        if (companyRevenue.companyName == "Neckar IT") {
          companyRevenue.projectCountsByMonth[month]?.let { count ->
            appendLine("($count${SpecialChars.Nbsp}Projekte)")
          }
        }
      }
    }
  }
}

fun getYearlyCompanyRevenueColumn(year: Year?): TableDataColumn<CompanyRevenue> {
  return TableDataColumn(
    id = "$year",
    title = "${year?.value ?: "Nicht abgerechnet"}",
    titleClasses = "justify-content-end",
    sortFunction = compareBy { it.revenueByYear[year] },
  ) { companyRevenue ->
    {
      attrs {
        addClass("text-end")
      }

      +buildString {
        companyRevenue.revenueByYear[year]?.let { revenue ->
          appendLine(revenue.format(plannerI18nConfiguration))
        }
        if (companyRevenue.companyName == "Neckar IT") {
          companyRevenue.projectCountsByYear[year]?.let { count ->
            appendLine("($count${SpecialChars.Nbsp}Projekte)")
          }
        }
      }
    }
  }
}
